import React, { useState, useEffect } from 'react';
import { bool, func, string } from 'prop-types';

import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';

import EsmsDialog from '../../components/EsmsDialog';

import * as ArchitectActions from '../../actions/architectActions';
import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as UiActions from '../../actions/uiActions';

import classes from './CreateEntityModal.module.css';

const ValidationSchema = Yup.object().shape({
    equipmentName: Yup.array()
        .required()
});

const CreateSystemEquipmentModal = ({
    close,
    clearErrors,
    companyInfo,
    open,
    equipment,
    filteredEquipment,
    selectedNode,
    selectedSite,
    title,
    isLoading,
    errors,
    toggleLoading,
    ...props
}) => {

    const [availableEquipment, setAvailableEquipment] = useState([]);

    useEffect(() => {
        const normalized = filteredEquipment.map(({ id }) => id);
        setAvailableEquipment(equipment.filter(value => !normalized.includes(value.id)));
    }, [equipment, filteredEquipment]);

    const formik = useFormik({
        initialValues: {
            equipmentName: []
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                createSystemEquipment
            } = props;

            let isCreated;

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isCreated = await createSystemEquipment(
                        selectedNode.id,
                        selectedSite.id,
                        formik.values.equipmentName
                    );
                }

                if (isCreated) {
                    setSubmitting(false);
                    toggleLoading(false);

                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();

        close();
    };

    const handleEquipmentName = () => {
        let ids = [...formik.values.equipmentName];
        let names = [];

        equipment.forEach(piece => {
            if (ids.includes(piece.id)) {
                names.push(piece.name);
            }
        });

        return names.join(', ');
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onRendered={formik.handleReset}
            onConfirm={formik.handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            confirmText="Add"
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/systemTreeIcon.svg" alt="system icon" />
                        <span>Add Equipment to System</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Select the pieces of Equipment to add to System.
                        </p>
                        <p>
                            Equipment already associated with this System will not appear in the list.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.fullWidth}>
                            <InputLabel
                                htmlFor="equipmentName"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.equipmentName) && Boolean(formik.touched.equipmentName)}
                            >
                                Select Equipment
                            </InputLabel>
                            <Select
                                multiple
                                value={formik.values.equipmentName}
                                id="equipmentName"
                                input={<Input id="select-multiple-checkbox" />}
                                renderValue={handleEquipmentName}
                                onChange={formik.handleChange}
                                name="equipmentName"
                                className={classes.input}
                                inputProps={{
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.equipmentName) && Boolean(formik.touched.equipmentName)}
                            >
                                {
                                    availableEquipment.map(piece => (
                                        <MenuItem
                                            className={classes.selectMenuItem}
                                            value={piece.id}
                                            key={piece.id}
                                        >
                                            <Checkbox
                                                color="primary"
                                                checked={formik.values.equipmentName.indexOf(piece.id) > -1}
                                            />
                                            <span>{piece.name}</span>
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                            {formik.touched.equipmentName &&
                                formik.errors.equipmentName && <FormHelperText className={classes.error}>{formik.errors.equipmentName}</FormHelperText>}
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog >
    );
};

CreateSystemEquipmentModal.propTypes = {
    close: func.isRequired,
    open:  bool.isRequired,
    title: string.isRequired
};

const mapStateToProps = state => {
    return {
        companyInfo:       state.companyInfo,
        equipment:         state.architect.equipment,
        errors:            state.messages.errors,
        filteredEquipment: state.architect.filteredEquipment,
        isLoading:         state.ui.isLoading,
        selectedNode:      state.architect.selectedNode,
        selectedSite:      state.site.selectedSite
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ApplicationMessageActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateSystemEquipmentModal);
